import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { DisplayPost } from '../components/DisplayPost';
import { LoadingText } from '../components/Style';
import useInfiniteScroll from '../hooks/useInfiniteScroll';

var FeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  align-items: center;
  max-width: 410px;
  margin: 0 auto;
`;

var TagHeader = styled.h1`
  font-weight: 900;
  font-size: 1.4rem;
  margin: 1rem 0 0.5rem 0;
  text-align: center;
`;

export default function TagFeed() {
  var { tagName } = useParams();

  useEffect(
    function () {
      if (tagName) {
        document.title = '#' + tagName + ' | HeavyRotation';
      }
    },
    [tagName]
  );

  var _error = useState(null);
  var error = _error[0];
  var setError = _error[1];

  var fetchPage = useCallback(
    function (cursor) {
      var url = '/api/tags/' + encodeURIComponent(tagName) + '/posts';
      var params = [];
      if (cursor) params.push('before=' + encodeURIComponent(cursor));
      params.push('limit=20');
      if (params.length) url += '?' + params.join('&');
      setError(null);
      return api
        .get(url)
        .then(function (response) {
          return response.data;
        })
        .catch(function (err) {
          setError(err);
          return { posts: [], hasMore: false, nextCursor: null };
        });
    },
    [tagName]
  );

  var scroll = useInfiniteScroll(fetchPage, [tagName]);
  var posts = scroll.posts;
  var loading = scroll.loading;
  var loadingMore = scroll.loadingMore;
  var sentinelRef = scroll.sentinelRef;
  var removePost = scroll.removePost;

  var _bookmarkedMap = useState({});
  var bookmarkedMap = _bookmarkedMap[0];
  var setBookmarkedMap = _bookmarkedMap[1];

  var _libraryAddMap = useState({});
  var libraryAddMap = _libraryAddMap[0];
  var setLibraryAddMap = _libraryAddMap[1];

  var _followedMap = useState({});
  var followedMap = _followedMap[0];
  var setFollowedMap = _followedMap[1];

  var _userReactionsMap = useState({});
  var userReactionsMap = _userReactionsMap[0];
  var setUserReactionsMap = _userReactionsMap[1];

  var _likedCommentsMap = useState({});
  var likedCommentsMap = _likedCommentsMap[0];
  var setLikedCommentsMap = _likedCommentsMap[1];

  var _repostedMap = useState({});
  var repostedMap = _repostedMap[0];
  var setRepostedMap = _repostedMap[1];

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens?.token;

  useEffect(
    function () {
      if (!isLoggedIn) return;
      api
        .get('/api/bookmarks')
        .then(function (response) {
          var map = {};
          var ids = response.data || [];
          for (var i = 0; i < ids.length; i++) {
            map[ids[i]] = true;
          }
          setBookmarkedMap(map);
        })
        .catch(function () {});
      api
        .get('/api/library-adds')
        .then(function (response) {
          var map = {};
          var ids = response.data || [];
          for (var i = 0; i < ids.length; i++) {
            map[ids[i]] = true;
          }
          setLibraryAddMap(map);
        })
        .catch(function () {});
      api
        .get('/api/follows')
        .then(function (response) {
          var map = {};
          var ids = response.data || [];
          for (var i = 0; i < ids.length; i++) {
            map[ids[i]] = true;
          }
          setFollowedMap(map);
        })
        .catch(function () {});
      api
        .get('/api/post-reactions')
        .then(function (response) {
          var map = {};
          var items = response.data || [];
          for (var i = 0; i < items.length; i++) {
            var pid = items[i].postId;
            if (!map[pid]) map[pid] = [];
            map[pid].push(items[i].emoji);
          }
          setUserReactionsMap(map);
        })
        .catch(function () {});
      api
        .get('/api/comment-likes')
        .then(function (response) {
          var map = {};
          var ids = response.data || [];
          for (var i = 0; i < ids.length; i++) {
            map[ids[i]] = true;
          }
          setLikedCommentsMap(map);
        })
        .catch(function () {});
      api
        .get('/api/reposts')
        .then(function (response) {
          var map = {};
          var ids = response.data || [];
          for (var i = 0; i < ids.length; i++) {
            map[ids[i]] = true;
          }
          setRepostedMap(map);
        })
        .catch(function () {});
    },
    [isLoggedIn]
  );

  function handleBookmarkToggle(postId, bookmarked) {
    setBookmarkedMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[postId] = bookmarked;
      return next;
    });
  }

  function handleReactionToggle(postId, emoji, reacted) {
    setUserReactionsMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = (prev[key] || []).slice();
        }
      }
      if (!next[postId]) next[postId] = [];
      if (reacted) {
        if (next[postId].indexOf(emoji) === -1) next[postId].push(emoji);
      } else {
        next[postId] = next[postId].filter(function (e) {
          return e !== emoji;
        });
      }
      return next;
    });
  }

  function handleFollowToggle(userId, following) {
    setFollowedMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[userId] = following;
      return next;
    });
  }

  function handleCommentLikeToggle(commentId, liked) {
    setLikedCommentsMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[commentId] = liked;
      return next;
    });
  }

  function handleRepostToggle(postId, reposted) {
    setRepostedMap(function (prev) {
      var next = {};
      for (var key in prev) {
        if (prev.hasOwnProperty(key)) {
          next[key] = prev[key];
        }
      }
      next[postId] = reposted;
      return next;
    });
  }

  function handlePostDelete(postId) {
    removePost(postId);
  }

  function renderPost(post) {
    return (
      <DisplayPost
        post={post}
        key={post.repostId || post.id}
        isBookmarked={!!bookmarkedMap[post.id]}
        userReactions={userReactionsMap[post.id] || []}
        isAddedToLibrary={!!libraryAddMap[post.id]}
        isFollowing={!!followedMap[post.user.id]}
        isReposted={!!repostedMap[post.id]}
        onBookmarkToggle={handleBookmarkToggle}
        onReactionToggle={handleReactionToggle}
        onFollowToggle={handleFollowToggle}
        followSource="tag_feed"
        likedCommentsMap={likedCommentsMap}
        onCommentLikeToggle={handleCommentLikeToggle}
        onPostDelete={handlePostDelete}
        onRepostToggle={handleRepostToggle}
      />
    );
  }

  if (loading) return <LoadingText>Loading...</LoadingText>;
  if (error) return <p>Tag not found.</p>;

  return (
    <FeedContainer>
      <TagHeader>#{tagName}</TagHeader>
      {posts.length > 0 ? (
        <>
          {posts.map(renderPost)}
          <div ref={sentinelRef} />
          {loadingMore && <LoadingText>Loading more...</LoadingText>}
        </>
      ) : (
        <p>No posts with this tag yet.</p>
      )}
    </FeedContainer>
  );
}
